import { useGetRecordRepresentation } from "ra-core";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";

import type { Company, Contact, Deal, Task } from "../types";

/** Reads as a bullet, announced as nothing: it separates, it does not inform. */
export const TaskMetaSeparator = () => (
  <span aria-hidden="true" className="opacity-60">
    ·
  </span>
);

/**
 * What the task is about: the account, then the deal, then the person.
 *
 * The account leads because that is what a task list is scanned by: an account
 * manager works accounts, and the deal and the person are levels of detail
 * down. It is read straight off the task — `company_id` is denormalised there
 * by the `set_task_company` trigger — so an account task with nobody named
 * still says which account it belongs to, and a contact task no longer pays for
 * a hop through the contact to name one.
 *
 * All three are optional: `tasks_has_target` only guarantees that at least one
 * of them is set. Each slot therefore renders nothing at all — not an empty
 * name, not a dangling separator, not a link to `/contacts/undefined/show` —
 * when its own reference is missing.
 */
export const TaskAccountFields = ({ task }: { task: Task }) => {
  const getContactRepresentation = useGetRecordRepresentation("contacts");

  return (
    <>
      <ReferenceField<Task, Company>
        source="company_id"
        reference="companies"
        record={task}
        link={false}
        render={({ referenceRecord }) => {
          const company = referenceRecord as Company | undefined;
          if (!company) return null;
          return (
            <>
              <Link
                to={`/companies/${company.id}/show`}
                className="font-medium text-foreground hover:underline"
              >
                {company.name}
              </Link>
              <TaskMetaSeparator />
            </>
          );
        }}
      />
      <ReferenceField<Task, Deal>
        source="deal_id"
        reference="deals"
        record={task}
        link={false}
        render={({ referenceRecord }) => {
          const deal = referenceRecord as Deal | undefined;
          if (!deal) return null;
          return (
            <>
              <Link to={`/deals/${deal.id}/show`} className="hover:underline">
                {deal.name}
              </Link>
              <TaskMetaSeparator />
            </>
          );
        }}
      />
      <ReferenceField<Task, Contact>
        source="contact_id"
        reference="contacts"
        record={task}
        link={false}
        render={({ referenceRecord }) => {
          const contact = referenceRecord as Contact | undefined;
          if (!contact) return null;
          return (
            <>
              <Link
                to={`/contacts/${contact.id}/show`}
                className="hover:underline"
              >
                {getContactRepresentation(contact)}
              </Link>
              <TaskMetaSeparator />
            </>
          );
        }}
      />
    </>
  );
};
